import React, { useState } from 'react';
import { FormBuilder, FieldControl, Validators } from 'react-reactive-form';
import { markAllControlsDirtyAndTouched } from '../helpers/form.helper';
import { passwordEyeIcon } from '../assets/superAdminAssets';
import { renderPasswordHelperText } from '../helpers/index.helper';
import { CookieNames, getCookie } from '../helpers/cookie.helper';
import { useUser } from '../store/hooks/useUser';
import { changePassword } from '../services/profile.service';
import { Constants } from '../constants';
import { useAppContext } from '../contexts/AppContext';
import uuid from 'react-uuid';

const ChangePasswordTab = () => {
  const { user } = useUser();
  const { showToaster, hideToaster, showLoader, hideLoader } = useAppContext();
  const [passwordForm] = useState(() =>
    FormBuilder.group({
      currentPassword: ['', Validators.required],
      newPassword: [
        '',
        [
          Validators.required,
          control => {
            const value = control.value || '';
            const length = value.length >= 8;
            const uppercase = /[A-Z]/.test(value);
            const lowercase = /[a-z]/.test(value);
            const numeric = /\d/.test(value);
            const special = /[@$%^&*!#]/.test(value);

            if (length && uppercase && lowercase && numeric && special) {
              return null;
            }

            return { weakPassword: true };
          },
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          control => {
            const parent = control.parent;
            if (!parent) return null;
            const newPassword = parent.get('newPassword')?.value;
            const confirmPassword = control.value;
            if (newPassword && confirmPassword && newPassword !== confirmPassword) {
              return { passwordMismatch: true };
            }
            return null;
          },
        ],
      ],
    })
  );

  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    numeric: false,
    special: false,
  });
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showErrorMessages, setShowErrorMessages] = useState(false);

  const validatePassword = (password) => {
    const validations = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numeric: /\d/.test(password),
      special: /[@$%^&*!#]/.test(password),
    };
    const errors = Object.keys(validations).filter((key) => !validations[key]);
    setPasswordValidations(validations);
    setPasswordErrors(errors);
    return errors;
  };

  const handleSubmit = async (e) => {
    try {
      showLoader();
      e.preventDefault();
      markAllControlsDirtyAndTouched(passwordForm);

      if (passwordForm.status === 'INVALID') {
        return;
      }

      const params = {
        userId: getCookie(CookieNames.USER_ID),
        CurrentPassword: passwordForm.get('currentPassword').value,
        NewPassword: passwordForm.get('newPassword').value,
        RoleName: getCookie(user.roleName),
        SessionId: getCookie(CookieNames.SESSION_ID) ?? uuid(),
        Id: 0,
        IsSetting: true
      };

      const response = await changePassword(params);
      if (response.status === 200) {
        showToaster(response.message, 'success');
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
        passwordForm.reset();
      } else {
        showToaster(response.message, 'error');
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      error.response.data.message && showToaster(error.response.data.message, 'error');
      !error.response.data.message && showToaster("Something went wrong while changing password. Please try again.", 'error');
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  };

  const togglePasswordVisibility = (inputId, eyeId) => {
    const input = document.getElementById(inputId);
    const eyeIcon = document.getElementById(eyeId);
    if (!input || !eyeIcon) return;

    input.type = input.type === 'password' ? 'text' : 'password';
    eyeIcon.classList.toggle('active_pass');
  };

  return (
    <div className="lm-card">
      <h3 className="lm-card-heading">Change Password</h3>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-4">
            <FieldControl
              strict={false}
              render={({ handler, touched, hasError }) => (
                <div className={`form-group input-password ${touched && hasError('required') ? 'error-field' : ''}`}>
                  <label htmlFor="current_password">
                    Current Password
                    <span className="asterisk">*</span>
                  </label>
                  <input
                    {...handler()}
                    type="password"
                    className="form-control"
                    id="current_password"
                    placeholder="Enter current password"
                  />
                  <img
                    onClick={() => togglePasswordVisibility('current_password', 'current_pass_eye')}
                    id="current_pass_eye"
                    className="pass_eye"
                    src={passwordEyeIcon}
                    alt="Toggle Current Password Visibility"
                  />
                  {touched && hasError('required') && (
                    <span className="error-message">Current password is required</span>
                  )}
                </div>
              )}
              control={passwordForm.get('currentPassword')}
            />
          </div>

          <div className="col-md-4">
            <FieldControl
              strict={false}
              render={({ handler, touched, hasError }) => (
                <div
                  className={`form-group input-password ${touched && (hasError('required') || hasError('weakPassword'))
                    ? 'error-field'
                    : ''
                    }`}
                >
                  <label htmlFor="new_password">
                    New Password
                    <span className="asterisk">*</span>
                  </label>
                  <div>
                    <input
                      {...handler()}
                      type="password"
                      className="form-control"
                      id="new_password"
                      placeholder="Enter new password"
                      onChange={(e) => {
                        handler().onChange(e);
                        validatePassword(e.target.value);
                        passwordForm.get('confirmPassword')?.updateValueAndValidity();
                        setShowErrorMessages(false);
                      }}
                    />
                    <img
                      onClick={() => togglePasswordVisibility('new_password', 'new_pass_eye')}
                      id="new_pass_eye"
                      className="pass_eye"
                      src={passwordEyeIcon}
                      alt="Toggle New Password Visibility"
                    />
                  </div>
                  {touched && hasError('required')
                    ? (
                      <span className="error-message">New password is required</span>
                    )
                    : touched && hasError('weakPassword')
                      ? (
                        <span className="error-message">
                          Password must be at least 8 characters, include upper and lowercase letters,
                          a number, and a special character.
                        </span>
                      )
                      : showErrorMessages && passwordErrors.length > 0
                        ? renderPasswordHelperText(passwordValidations)
                        : null}
                </div>
              )}
              control={passwordForm.get('newPassword')}
            />
          </div>

          <div className="col-md-4">
            <FieldControl
              strict={false}
              render={({ handler, touched, hasError }) => (
                <div
                  className={`form-group input-password ${touched && (hasError('required') || hasError('passwordMismatch'))
                    ? 'error-field'
                    : ''
                    }`}
                >
                  <label htmlFor="confirm_password">
                    Confirm Password
                    <span className="asterisk">*</span>
                  </label>
                  <input
                    {...handler()}
                    type="password"
                    className="form-control"
                    id="confirm_password"
                    placeholder="Confirm new password"
                  />
                  <img
                    onClick={() => togglePasswordVisibility('confirm_password', 'confirm_pass_eye')}
                    id="confirm_pass_eye"
                    className="pass_eye"
                    src={passwordEyeIcon}
                    alt="Toggle Confirm Password Visibility"
                  />
                  {touched && hasError('required') && (
                    <span className="error-message">Confirm password is required</span>
                  )}
                  {touched && hasError('passwordMismatch') && (
                    <span className="error-message">Passwords do not match</span>
                  )}
                </div>
              )}
              control={passwordForm.get('confirmPassword')}
            />
          </div>
        </div>

        <div className="justify-content-start d-flex gap-10">
          <button type="submit" className="lm-button1">
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordTab;

